import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { SOPS } from "@/constants/testIds";
import { logError } from "@/lib/logger";
import { ResourcesPanel } from "@/components/guidelines/ResourcesPanel";

function SopList({ items }) {
  if (!items.length) {
    return <p className="text-zinc-500 text-center py-10">No guidelines available yet.</p>;
  }
  return (
    <div className="space-y-4">
      {items.map((s) => (
        <div key={s.id} data-testid={SOPS.item(s.id)} className="bg-white border border-zinc-200 rounded-sm p-6">
          <h3 className="font-display font-bold text-lg text-zinc-950 mb-4">{s.title}</h3>
          <ul className="space-y-3">
            {s.steps.map((step, i) => (
              <li key={`${s.id}-step-${i}`} className="flex gap-3 text-sm text-zinc-600 leading-relaxed">
                <CheckCircle2 className="h-4 w-4 text-[#1E3A6E] shrink-0 mt-0.5" />
                {step}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function Guidelines() {
  const [searchParams] = useSearchParams();
  const [during, setDuring] = useState([]);
  const [after, setAfter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") === "resources" ? "resources" : "during");

  useEffect(() => {
    (async () => {
      try {
        const [d, a] = await Promise.all([
          api.get("/sops", { params: { phase: "during" } }),
          api.get("/sops", { params: { phase: "after" } }),
        ]);
        setDuring(d.data);
        setAfter(a.data);
      } catch (e) {
        logError(e);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (activeTab === "resources" && window.location.hash === "#volunteer-section") {
      const timer = setTimeout(() => {
        document.getElementById("volunteer-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [activeTab, loading]);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A6E] mb-2">Be Ready</p>
      <h1 className="font-display font-extrabold uppercase text-3xl md:text-4xl text-zinc-950 tracking-tight mb-4">Guidelines &amp; Guidance</h1>
      <p className="text-zinc-600 leading-relaxed mb-10 max-w-2xl">
        Clear, plain-language steps for what to do while something is happening, and what to do right after.
        Read these before you ever need them.
      </p>

      {loading ? (
        <div className="flex justify-center py-14"><div className="h-8 w-8 border-2 border-zinc-300 border-t-zinc-950 rounded-full animate-spin" /></div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-zinc-200 mb-8">
            <TabsTrigger value="during" data-testid={SOPS.tabDuring} className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white font-bold uppercase text-xs">
              During an Event
            </TabsTrigger>
            <TabsTrigger value="after" data-testid={SOPS.tabAfter} className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white font-bold uppercase text-xs">
              After an Event
            </TabsTrigger>
            <TabsTrigger value="resources" data-testid={SOPS.tabResources} className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white font-bold uppercase text-xs">
              Resources
            </TabsTrigger>
          </TabsList>
          <TabsContent value="during"><SopList items={during} /></TabsContent>
          <TabsContent value="after"><SopList items={after} /></TabsContent>
          <TabsContent value="resources"><ResourcesPanel /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}
